import { useState, useEffect } from 'react';
import { api, type UserProgress } from '../utils/api';

interface ProgressProps {
  userId: string;
}

export const Progress = ({ userId }: ProgressProps) => {
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadProgress();
  }, [userId]);

  const loadProgress = async () => {
    setIsLoading(true);
    try {
      const data = await api.getProgress(userId);
      setProgress(data);
    } catch (error) {
      console.error('Error loading progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingSection}>
          <div style={styles.loader}></div>
          <p>Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (!progress) {
    return (
      <div style={styles.container}>
        <p>Failed to load progress. Please try again.</p>
      </div>
    );
  }

  const passingRate =
    progress.quizzesTaken > 0
      ? Math.round((progress.quizzesPassedCount / progress.quizzesTaken) * 100)
      : 0;

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Your Progress</h2>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{progress.totalSessions}</div>
          <div style={styles.statLabel}>Study Sessions</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statValue}>{progress.studyStreak}</div>
          <div style={styles.statLabel}>Day Streak</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statValue}>{progress.topicsStudied.length}</div>
          <div style={styles.statLabel}>Topics Studied</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statValue}>{progress.quizzesTaken}</div>
          <div style={styles.statLabel}>Quizzes Taken</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statValue}>{passingRate}%</div>
          <div style={styles.statLabel}>Passing Rate</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statValue}>{progress.achievements.length}</div>
          <div style={styles.statLabel}>Achievements</div>
        </div>
      </div>

      {progress.topicsStudied.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Topics You've Studied</h3>
          <div style={styles.topicsList}>
            {progress.topicsStudied.map((topic, index) => (
              <div key={index} style={styles.topicTag}>
                {topic}
              </div>
            ))}
          </div>
        </div>
      )}

      {progress.achievements.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Achievements</h3>
          <div style={styles.achievementsList}>
            {progress.achievements.map((achievement, index) => (
              <div key={index} style={styles.achievementCard}>
                <div style={styles.achievementIcon}>🏆</div>
                <div style={styles.achievementName}>{achievement}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {progress.totalSessions === 0 && (
        <div style={styles.emptyState}>
          <p>Start your first study session to see your progress!</p>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '30px',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  loadingSection: {
    textAlign: 'center',
    marginTop: '60px',
  },
  loader: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    margin: '0 auto 20px',
    animation: 'spin 1s linear infinite',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '20px',
    marginBottom: '40px',
  },
  statCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  statValue: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#007bff',
    marginBottom: '8px',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
  },
  section: {
    marginBottom: '40px',
  },
  sectionTitle: {
    marginBottom: '20px',
    fontSize: '20px',
  },
  topicsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
  },
  topicTag: {
    padding: '8px 16px',
    backgroundColor: '#e3f2fd',
    borderRadius: '20px',
    fontSize: '14px',
    color: '#007bff',
    fontWeight: '500',
  },
  achievementsList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
  },
  achievementCard: {
    backgroundColor: '#fff3cd',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  achievementIcon: {
    fontSize: '32px',
    marginBottom: '8px',
  },
  achievementName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#856404',
  },
  emptyState: {
    textAlign: 'center',
    color: '#666',
    marginTop: '60px',
  },
};
